import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Alert,
  AlertDescription,
  Badge
} from '../components/ui';
import { CheckCircle, AlertTriangle, MapPin, FileText, Building, Package } from 'lucide-react';
import { USAContext } from './USAContext';

const API_URL = import.meta.env.VITE_API_URL;

const ValidateShipmentInfo = ({ setIsStepValidated }) => {
    const { shipmentInfo, selectedPlan, handleNext } = useContext(USAContext);
    const [pdfUrls, setPdfUrls] = useState([]); // Store URLs for all shipment labels
    const [shipmentAddresses, setShipmentAddresses] = useState({}); // Unique address fields for each shipment
    const [confirmedShipments, setConfirmedShipments] = useState([]); // Track confirmed shipments
    const [adjustedAddresses, setAdjustedAddresses] = useState({}); // Store adjusted addresses from the database
    const navigate = useNavigate();

    // Fetch shipment labels and adjusted addresses for all shipments in the confirmed placement
    useEffect(() => {
        if (selectedPlan) {
            fetchShipmentLabels();
            fetchAdjustedAddresses();
        }
    }, [selectedPlan]);

    
    useEffect(() => {
        if (pdfUrls.length > 0) {
            fetchAdjustedAddresses();
        }
    }, [pdfUrls]); // Add pdfUrls as a dependency
    
    const fetchShipmentLabels = async () => {
        if (!selectedPlan || !selectedPlan.placement_options) return;

        const confirmedPlacement = selectedPlan.placement_options.find(option => option.confirmed_at);
        if (!confirmedPlacement) {
            console.error("No confirmed placement option found.");
            return;
        }

        const shipmentLabels = [];
        for (const shipment of confirmedPlacement.shipments) {
            try {
                const response = await fetch(`https://app.yyzprep.ca/api/fba-transport/v2024/plans/${selectedPlan.id}/labels?shipment_id=${shipment.shipment_id}&api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`);
                if (!response.ok) {
                    throw new Error(`Failed to fetch labels for shipment ID: ${shipment.shipment_id}`);
                }
                const data = await response.json();
        
                // Try to find the 4" x 6" package label first
                let label = data.data.find(label => label.name.includes('4" x 6"') && label.type === 'package');
        
                // If 4" x 6" label is not found, fall back to the 8.5" x 11" label
                if (!label) {
                    label = data.data.find(label => label.name.includes('8.5') && label.type === 'package');
                }
        
                // If a label is found, add it to the shipmentLabels array
                if (label) {
                    shipmentLabels.push({
                        shipmentId: shipment.shipment_id,
                        url: label.url,
                        address: shipment.destination.address,
                        warehouseId: shipment.destination.warehouse_id
                    });
                } else {
                    console.error("No suitable label found for shipment ID: " + shipment.shipment_id);
                }
            } catch (error) {
                console.error('Error fetching labels for shipment ID:', shipment.shipment_id, error);
            }
        }

        if (shipmentLabels.length > 0) {
            setPdfUrls(shipmentLabels);
        } else {
            console.error("No valid 4x6 package labels found for any shipment.");
        }
    };

    const fetchAdjustedAddresses = async () => {
        try {
            const response = await fetch(`${API_URL}/api/warehouse-addresses`);
            const data = await response.json();
            const adjustedAddressesMap = {};
    
            // Populate adjustedAddressesMap with data from the database
            data.forEach(address => {
                adjustedAddressesMap[address.warehouse_id] = address;
            });
    
            // Initialize shipmentAddresses with adjusted or original addresses
            const updatedAddresses = {};
            pdfUrls.forEach(shipment => {
                const adjustedAddress = adjustedAddressesMap[shipment.warehouseId];
                if (adjustedAddress) {
                    // Use adjusted address from the database
                    updatedAddresses[shipment.shipmentId] = {
                        address: adjustedAddress.address_line_1,
                        city: adjustedAddress.city,
                        state: adjustedAddress.state_or_province_code,
                        zip: adjustedAddress.postal_code,
                    };
                } else {
                    // Use original address from the API
                    updatedAddresses[shipment.shipmentId] = {
                        address: shipment.address.address_line_1 || '',
                        city: shipment.address.city || '',
                        state: shipment.address.state_or_province_code || '',
                        zip: shipment.address.postal_code || '',
                    };
                }
            });
    
            // Update shipmentAddresses state
            setShipmentAddresses(updatedAddresses);
            setAdjustedAddresses(adjustedAddressesMap);
        } catch (error) {
            console.error('Error fetching adjusted addresses:', error);
        }
    };
    // Handle address changes for a specific shipment
    const handleAddressChange = (shipmentId, field, value) => {
        setShipmentAddresses((prev) => ({
            ...prev,
            [shipmentId]: {
                ...prev[shipmentId],
                [field]: value,
            },
        }));
    };

    // Handle shipment confirmation
    const handleConfirmShipment = async (shipmentId, warehouseId, originalAddress) => {
        const address = shipmentAddresses[shipmentId];

        // Check if the address has been edited
        const isAddressEdited = (
            address.address !== originalAddress.address_line_1 ||
            address.city !== originalAddress.city ||
            address.state !== originalAddress.state_or_province_code ||
            address.zip !== originalAddress.postal_code
        );

        if (isAddressEdited) {
            try {
                await fetch(`${API_URL}/api/warehouse-addresses`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        warehouse_id: warehouseId,
                        address_line_1: address.address,
                        city: address.city,
                        state_or_province_code: address.state,
                        postal_code: address.zip,
                        is_adjusted: true,
                    }),
                });
            } catch (error) {
                console.error('Error confirming shipment:', error);
                return;
            }
        }

        setConfirmedShipments((prev) => [...prev, shipmentId]);
    };

    // Check if all shipments are confirmed
    const allShipmentsConfirmed = pdfUrls.length > 0 && pdfUrls.every(shipment => confirmedShipments.includes(shipment.shipmentId));

    // Handle validation and proceed to the next step
    const handleValidate = () => {
        if (allShipmentsConfirmed) {
            console.log('All shipments confirmed and validated.');
            setIsStepValidated(true);
            handleNext(); // Proceed to the next step
        } else {
            console.error('Not all shipments are confirmed.');
        }
    };

    // Update where you handle photos in the component
    const handlePhotoDisplay = (photos) => {
        if (!Array.isArray(photos)) return [];
        
        return photos.map(photo => {
            // Handle both old format (string) and new format (object)
            return typeof photo === 'string' ? { url: photo } : photo;
        });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center space-x-2 mb-6">
                <Package className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900">Validate Shipment Information</h2>
            </div>

            {/* Shipment Labels Section */}
            {pdfUrls.length > 0 && (
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-slate-900 flex items-center space-x-2">
                        <FileText className="w-5 h-5" />
                        <span>Shipment Labels</span>
                    </h3>
                    
                    <div className="grid gap-6">
                        {pdfUrls.map((shipment, index) => {
                            const adjustedAddress = adjustedAddresses[shipment.warehouseId];
                            const isAdjusted = adjustedAddress && adjustedAddress.is_adjusted;

                            return (
                                <Card key={index}>
                                    <CardHeader>
                                        <div className="flex items-center space-x-3">
                                            <Badge variant="secondary" className="text-xs">
                                                #{index + 1}
                                            </Badge>
                                            <h4 className="text-lg font-medium">Shipment ID: {shipment.shipmentId}</h4>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {/* PDF Viewer Section */}
                                            <div>
                                                <h5 className="text-sm font-medium mb-3 flex items-center space-x-2">
                                                    <FileText className="w-4 h-4" />
                                                    <span>Shipping Label</span>
                                                </h5>
                                                {shipment.url ? (
                                                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                                                        <embed
                                                            src={shipment.url}
                                                            width="100%"
                                                            height="500px"
                                                            type="application/pdf"
                                                            className="w-full"
                                                            onLoad={() => window.scrollTo(0, 0)}
                                                        />
                                                    </div>
                                                ) : (
                                                    <Alert className="border-red-200 bg-red-50">
                                                        <AlertTriangle className="h-4 w-4 text-red-600" />
                                                        <AlertDescription className="text-red-700">
                                                            No 4x6 package label found for this shipment.
                                                        </AlertDescription>
                                                    </Alert>
                                                )}
                                            </div>

                                            {/* Address Form Section */}
                                            <div>
                                                <div className="flex items-center justify-between mb-3">
                                                    <h5 className="text-sm font-medium flex items-center space-x-2">
                                                        <MapPin className="w-4 h-4" />
                                                        <span>Destination Address</span>
                                                    </h5>
                                                    {isAdjusted && (
                                                        <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                                                            Adjusted Address
                                                        </Badge>
                                                    )}
                                                </div>
                                                
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                                            Address Line 1 *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            value={shipmentAddresses[shipment.shipmentId]?.address || ''}
                                                            onChange={(e) => handleAddressChange(shipment.shipmentId, 'address', e.target.value)}
                                                            required
                                                        />
                                                    </div>
                                                    
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                                                City *
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                value={shipmentAddresses[shipment.shipmentId]?.city || ''}
                                                                onChange={(e) => handleAddressChange(shipment.shipmentId, 'city', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                        
                                                        <div>
                                                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                                                State *
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                value={shipmentAddresses[shipment.shipmentId]?.state || ''}
                                                                onChange={(e) => handleAddressChange(shipment.shipmentId, 'state', e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                                            ZIP Code *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            value={shipmentAddresses[shipment.shipmentId]?.zip || ''}
                                                            onChange={(e) => handleAddressChange(shipment.shipmentId, 'zip', e.target.value)}
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                {/* Confirmation Section */}
                                                <div className="mt-6 pt-4 border-t border-slate-200">
                                                    {confirmedShipments.includes(shipment.shipmentId) ? (
                                                        <div className="flex items-center space-x-2 text-green-600">
                                                            <CheckCircle className="w-5 h-5" />
                                                            <span className="font-medium">Shipment Confirmed</span>
                                                        </div>
                                                    ) : (
                                                        <Button
                                                            onClick={() => handleConfirmShipment(shipment.shipmentId, shipment.warehouseId, shipment.address)}
                                                            disabled={confirmedShipments.includes(shipment.shipmentId)}
                                                            className="bg-blue-600 hover:bg-blue-700 flex items-center space-x-2"
                                                        >
                                                            <CheckCircle className="w-4 h-4" />
                                                            <span>Confirm Shipment</span>
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Validation Status and Actions */}
            {pdfUrls.length > 0 && (
                <Card className="bg-slate-50">
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                {allShipmentsConfirmed ? (
                                    <div className="flex items-center space-x-2 text-green-600">
                                        <CheckCircle className="w-5 h-5" />
                                        <span className="font-medium">All shipments confirmed</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center space-x-2 text-orange-600">
                                        <AlertTriangle className="w-5 h-5" />
                                        <span className="font-medium">
                                            {confirmedShipments.length} of {pdfUrls.length} shipments confirmed
                                        </span>
                                    </div>
                                )}
                            </div>
                            
                            <Button
                                onClick={handleValidate}
                                disabled={!allShipmentsConfirmed}
                                className={`flex items-center space-x-2 ${
                                    allShipmentsConfirmed 
                                        ? 'bg-green-600 hover:bg-green-700' 
                                        : 'bg-slate-400 cursor-not-allowed'
                                }`}
                            >
                                <CheckCircle className="w-4 h-4" />
                                <span>Validate and Proceed</span>
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default ValidateShipmentInfo;